import { authFetch } from '@/lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Loader2, Trash2, ChevronDown, ChevronRight, Check, Minus, Ban, Calendar, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const statusIcon = (status) => {
    if (status === 'fait') return <span className="w-4 h-4 rounded bg-emerald-500 text-white flex items-center justify-center flex-shrink-0"><Check className="w-2.5 h-2.5" /></span>;
    if (status === 'pas_necessaire') return <span className="w-4 h-4 rounded bg-amber-400 text-white flex items-center justify-center flex-shrink-0"><Minus className="w-2.5 h-2.5" /></span>;
    if (status === 'a_ne_pas_faire') return <span className="w-4 h-4 rounded bg-red-500 text-white flex items-center justify-center flex-shrink-0"><Ban className="w-2.5 h-2.5" /></span>;
    return <span className="w-4 h-4 rounded bg-slate-200 flex-shrink-0" />;
};

const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export default function GuideChecklistResultsPage() {
    const navigate = useNavigate();
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    const fetchResults = useCallback(async () => {
        try {
            const site = JSON.parse(localStorage.getItem('guide_selected_site') || 'null');
            const siteParam = site?.id ? `&site_id=${site.id}` : '';
            const res = await authFetch(`${API}/api/guide/checklist-results?slug=checklist-depart${siteParam}&limit=50`);
            const data = await res.json();
            setResults(data.results || []);
            setTotal(data.total || 0);
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchResults(); }, [fetchResults]);

    const deleteResult = async (id) => {
        if (!window.confirm('Supprimer ce resultat ?')) return;
        try {
            await authFetch(`${API}/api/guide/checklist-results/${id}`, { method: 'DELETE' });
            setResults(prev => prev.filter(r => r.id !== id));
            setTotal(prev => prev - 1);
            toast.success('Supprime');
        } catch { toast.error('Erreur'); }
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    return (
        <div className="px-4 py-5" data-testid="guide-checklist-results">
            <button onClick={() => navigate('/guide/checklist')} className="flex items-center gap-1 text-sm text-slate-500 mb-4 active:text-slate-700">
                <ArrowLeft className="w-4 h-4" /> Fiches
            </button>

            <h1 className="text-lg font-bold text-slate-900 mb-1">Historique des fiches de depart</h1>
            <p className="text-xs text-slate-500 mb-4">{total} resultat{total > 1 ? 's' : ''} enregistre{total > 1 ? 's' : ''}</p>

            {results.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm">Aucun resultat enregistre.</div>
            ) : (
                <div className="space-y-2">
                    {results.map(r => {
                        const isOpen = expanded === r.id;
                        const pct = r.total_items > 0 ? Math.round((r.answered_count / r.total_items) * 100) : 0;
                        return (
                            <div key={r.id} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                                <button
                                    onClick={() => setExpanded(isOpen ? null : r.id)}
                                    className="w-full px-3 py-2.5 flex items-center gap-2 text-left"
                                    data-testid={`result-row-${r.id}`}
                                >
                                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <User className="w-3 h-3 text-slate-400" />
                                            <span className="text-sm font-medium text-slate-800 truncate">{r.user_name}</span>
                                        </div>
                                        <div className="flex items-center gap-3 mt-0.5">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3 text-slate-400" />
                                                <span className="text-[11px] text-slate-500">{formatDate(r.date)}</span>
                                            </span>
                                            {r.site_name && (
                                                <span className="text-[11px] text-red-600 font-medium">{r.site_name}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${pct === 100 ? 'bg-emerald-100 text-emerald-700' : pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                            {r.answered_count}/{r.total_items}
                                        </span>
                                    </div>
                                </button>
                                {isOpen && (
                                    <div className="border-t border-slate-100 px-3 py-2">
                                        {(r.sections_summary || []).map((s, si) => (
                                            <div key={si} className="mb-2">
                                                <div className="text-[11px] font-semibold text-indigo-700 mb-1">{s.title}</div>
                                                <div className="space-y-0.5">
                                                    {(s.items || []).map((item, ii) => (
                                                        <div key={ii} className={`${item.parent ? 'pl-4' : 'pl-1'}`}>
                                                            <div className="flex items-center gap-1.5">
                                                                {statusIcon(item.status)}
                                                                <span className="text-[11px] text-slate-600 leading-tight">
                                                                    {item.parent && <span className="text-slate-400">{item.parent} &gt; </span>}
                                                                    {item.label}
                                                                </span>
                                                            </div>
                                                            {item.note && (
                                                                <div className="ml-5 mt-0.5 text-[10px] text-indigo-600 italic" data-testid={`result-note-${si}-${ii}`}>
                                                                    Note : {item.note}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex justify-end pt-1 border-t border-slate-50 mt-2">
                                            <button onClick={() => deleteResult(r.id)} className="text-red-400 hover:text-red-600 flex items-center gap-1 text-[10px]" data-testid={`delete-result-${r.id}`}>
                                                <Trash2 className="w-3 h-3" /> Supprimer
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
